const BEIJING_TIMEZONE = 'Asia/Shanghai';
const MYSQL_BEIJING_TIMEZONE = '+08:00';
const BEIJING_OFFSET_HOURS = 8;
const MYSQL_SESSION_TIMEZONE_READY = Symbol.for('badminton.mysql.session.timezone');
const MYSQL_POOL_TIMEZONE_WIRED = Symbol.for('badminton.mysql.pool.session.timezone');

const DATE_ONLY_RE = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/;
const DATETIME_RE = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.(\d{1,6}))?$/;
const TIME_ONLY_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

const BEIJING_PARTS_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: BEIJING_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
  hourCycle: 'h23',
});

function pad2(value) {
  return String(value).padStart(2, '0');
}

function normalizeInteger(raw, fallback = 0) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.trunc(n);
}

function normalizeHourValue(raw, fallback = 0) {
  const hour = normalizeInteger(raw, fallback);
  return hour === 24 ? 0 : hour;
}

function isValidDateParts(parts) {
  if (!parts) return false;
  const year = normalizeInteger(parts.year, NaN);
  const month = normalizeInteger(parts.month, NaN);
  const day = normalizeInteger(parts.day, NaN);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return false;
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const dt = new Date(Date.UTC(year, month - 1, day));
  return dt.getUTCFullYear() === year
    && dt.getUTCMonth() === month - 1
    && dt.getUTCDate() === day;
}

function isValidTimeParts(parts) {
  if (!parts) return false;
  const hour = normalizeHourValue(parts.hour, 0);
  const minute = normalizeInteger(parts.minute, 0);
  const second = normalizeInteger(parts.second, 0);
  if (hour < 0 || hour > 23) return false;
  if (minute < 0 || minute > 59) return false;
  if (second < 0 || second > 59) return false;
  return true;
}

function parseDateOnlyParts(raw) {
  const text = String(raw || '').trim();
  const match = text.match(DATE_ONLY_RE);
  if (!match) return null;
  const parts = {
    year: normalizeInteger(match[1], NaN),
    month: normalizeInteger(match[2], NaN),
    day: normalizeInteger(match[3], NaN),
  };
  return isValidDateParts(parts) ? parts : null;
}

function parseNaiveDateTimeParts(raw) {
  const text = String(raw || '').trim();
  const match = text.match(DATETIME_RE);
  if (!match) return null;
  const parts = {
    year: normalizeInteger(match[1], NaN),
    month: normalizeInteger(match[2], NaN),
    day: normalizeInteger(match[3], NaN),
    hour: normalizeInteger(match[4], 0),
    minute: normalizeInteger(match[5], 0),
    second: normalizeInteger(match[6], 0),
    ms: Number(String(match[7] || '0').slice(0, 3).padEnd(3, '0')),
  };
  if (!isValidDateParts(parts) || !isValidTimeParts(parts)) return null;
  return parts;
}

function parseTimeOnlyParts(raw) {
  const text = String(raw || '').trim();
  const match = text.match(TIME_ONLY_RE);
  if (!match) return null;
  const parts = {
    hour: normalizeHourValue(match[1], 0),
    minute: normalizeInteger(match[2], 0),
    second: normalizeInteger(match[3], 0),
  };
  return isValidTimeParts(parts) ? parts : null;
}

function normalizeDateKeyFromParts(parts) {
  if (!isValidDateParts(parts)) return '';
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
}

function normalizeDateTimeFromParts(parts, includeSeconds = true) {
  if (!isValidDateParts(parts) || !isValidTimeParts(parts)) return '';
  const time = includeSeconds
    ? `${pad2(parts.hour)}:${pad2(parts.minute)}:${pad2(parts.second)}`
    : `${pad2(parts.hour)}:${pad2(parts.minute)}`;
  return `${normalizeDateKeyFromParts(parts)} ${time}`;
}

function normalizeTimeFromParts(parts, includeSeconds = true) {
  if (!isValidTimeParts(parts)) return '';
  return includeSeconds
    ? `${pad2(parts.hour)}:${pad2(parts.minute)}:${pad2(parts.second)}`
    : `${pad2(parts.hour)}:${pad2(parts.minute)}`;
}

function buildTimestampFromBeijingParts(parts) {
  if (!isValidDateParts(parts) || !isValidTimeParts(parts)) return NaN;
  return Date.UTC(
    normalizeInteger(parts.year, 0),
    normalizeInteger(parts.month, 1) - 1,
    normalizeInteger(parts.day, 1),
    normalizeInteger(parts.hour, 0) - BEIJING_OFFSET_HOURS,
    normalizeInteger(parts.minute, 0),
    normalizeInteger(parts.second, 0),
    normalizeInteger(parts.ms, 0),
  );
}

function toInstantDate(input) {
  if (input instanceof Date) {
    return Number.isFinite(input.getTime()) ? new Date(input.getTime()) : null;
  }
  if (typeof input === 'number') {
    const dt = new Date(input);
    return Number.isFinite(dt.getTime()) ? dt : null;
  }
  if (typeof input === 'string') {
    const text = input.trim();
    if (!text) return null;
    const naiveDateTime = parseNaiveDateTimeParts(text);
    if (naiveDateTime) {
      const ts = buildTimestampFromBeijingParts(naiveDateTime);
      return Number.isFinite(ts) ? new Date(ts) : null;
    }
    const dateOnly = parseDateOnlyParts(text);
    if (dateOnly) {
      const ts = buildTimestampFromBeijingParts({
        ...dateOnly,
        hour: 0,
        minute: 0,
        second: 0,
        ms: 0,
      });
      return Number.isFinite(ts) ? new Date(ts) : null;
    }
    const dt = new Date(text);
    return Number.isFinite(dt.getTime()) ? dt : null;
  }
  return null;
}

function getBeijingParts(input = Date.now()) {
  const dt = toInstantDate(input);
  if (!dt) return null;
  const dict = {};
  for (const part of BEIJING_PARTS_FORMATTER.formatToParts(dt)) {
    if (part.type === 'literal') continue;
    dict[part.type] = part.value;
  }
  const out = {
    year: normalizeInteger(dict.year, NaN),
    month: normalizeInteger(dict.month, NaN),
    day: normalizeInteger(dict.day, NaN),
    hour: normalizeHourValue(dict.hour, 0),
    minute: normalizeInteger(dict.minute, 0),
    second: normalizeInteger(dict.second, 0),
  };
  if (!isValidDateParts(out) || !isValidTimeParts(out)) return null;
  return out;
}

function getBeijingDateKey(input = Date.now(), fallback = '') {
  if (typeof input === 'string') {
    const dateOnly = parseDateOnlyParts(input);
    if (dateOnly) return normalizeDateKeyFromParts(dateOnly);
    const naiveDateTime = parseNaiveDateTimeParts(input);
    if (naiveDateTime) return normalizeDateKeyFromParts(naiveDateTime);
  }
  const parts = getBeijingParts(input);
  return parts ? normalizeDateKeyFromParts(parts) : fallback;
}

function formatBeijingDateTime(input, options = {}) {
  const { fallback = '', includeSeconds = true } = options || {};
  if (typeof input === 'string') {
    const naiveDateTime = parseNaiveDateTimeParts(input);
    if (naiveDateTime) return normalizeDateTimeFromParts(naiveDateTime, includeSeconds);
  }
  const parts = getBeijingParts(input);
  return parts ? normalizeDateTimeFromParts(parts, includeSeconds) : fallback;
}

function normalizeTimeOnlyString(input, options = {}) {
  const { fallback = '', includeSeconds = false } = options || {};
  if (typeof input === 'string') {
    const timeOnly = parseTimeOnlyParts(input);
    if (timeOnly) return normalizeTimeFromParts(timeOnly, includeSeconds);
    const naiveDateTime = parseNaiveDateTimeParts(input);
    if (naiveDateTime) return normalizeTimeFromParts(naiveDateTime, includeSeconds);
  }
  const parts = getBeijingParts(input);
  return parts ? normalizeTimeFromParts(parts, includeSeconds) : fallback;
}

function formatBeijingMonthDay(input, fallback = '') {
  let dateKey = '';
  if (typeof input === 'string') {
    const dateOnly = parseDateOnlyParts(input);
    if (dateOnly) dateKey = normalizeDateKeyFromParts(dateOnly);
    const naiveDateTime = parseNaiveDateTimeParts(input);
    if (!dateKey && naiveDateTime) dateKey = normalizeDateKeyFromParts(naiveDateTime);
  }
  if (!dateKey) {
    dateKey = getBeijingDateKey(input, '');
  }
  if (!dateKey) return fallback;
  const parts = parseDateOnlyParts(dateKey);
  if (!parts) return fallback;
  return `${parts.month}月${parts.day}日`;
}

function formatBeijingChineseDate(input, options = {}) {
  const { fallback = '', yearDigits = 4 } = options || {};
  let dateKey = '';
  if (typeof input === 'string') {
    const dateOnly = parseDateOnlyParts(input);
    if (dateOnly) dateKey = normalizeDateKeyFromParts(dateOnly);
    const naiveDateTime = parseNaiveDateTimeParts(input);
    if (!dateKey && naiveDateTime) dateKey = normalizeDateKeyFromParts(naiveDateTime);
  }
  if (!dateKey) {
    dateKey = getBeijingDateKey(input, '');
  }
  if (!dateKey) return fallback;
  const parts = parseDateOnlyParts(dateKey);
  if (!parts) return fallback;
  const year = yearDigits === 2 ? String(parts.year).slice(-2) : String(parts.year);
  return `${year}年${parts.month}月${parts.day}日`;
}

function getBeijingCompactTimestamp(input = Date.now(), fallback = '') {
  const parts = getBeijingParts(input);
  if (!parts) return fallback;
  return `${parts.year}${pad2(parts.month)}${pad2(parts.day)}${pad2(parts.hour)}${pad2(parts.minute)}${pad2(parts.second)}`;
}

function normalizeDateOnlyToBeijingString(input, fallback = null) {
  const dateKey = getBeijingDateKey(input, '');
  return dateKey || fallback;
}

function addDaysToDateKey(dateKey, days) {
  const parts = parseDateOnlyParts(dateKey);
  if (!parts) return '';
  const dt = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  dt.setUTCDate(dt.getUTCDate() + normalizeInteger(days, 0));
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}

function getFutureBeijingDateKey(daysLater = 0, baseInput = Date.now(), fallback = '') {
  const baseDateKey = getBeijingDateKey(baseInput, '');
  if (!baseDateKey) return fallback;
  const nextDateKey = addDaysToDateKey(baseDateKey, daysLater);
  return nextDateKey || fallback;
}

function buildBeijingFutureExpiryMs(baseInput = Date.now(), options = {}) {
  const {
    daysLater = 14,
    hour = 4,
    minute = 0,
    second = 0,
  } = options || {};
  const baseDateKey = getBeijingDateKey(baseInput, '');
  if (!baseDateKey) return NaN;
  const targetDateKey = addDaysToDateKey(baseDateKey, daysLater);
  const dateParts = parseDateOnlyParts(targetDateKey);
  if (!dateParts) return NaN;
  return buildTimestampFromBeijingParts({
    ...dateParts,
    hour: normalizeInteger(hour, 0),
    minute: normalizeInteger(minute, 0),
    second: normalizeInteger(second, 0),
    ms: 0,
  });
}

function toTimestampMs(input) {
  const dt = toInstantDate(input);
  return dt ? dt.getTime() : NaN;
}

async function ensureMysqlSessionTimeZone(connection) {
  if (!connection || typeof connection.query !== 'function') return;
  if (connection[MYSQL_SESSION_TIMEZONE_READY] === MYSQL_BEIJING_TIMEZONE) return;
  await connection.query('SET time_zone = ?', [MYSQL_BEIJING_TIMEZONE]);
  connection[MYSQL_SESSION_TIMEZONE_READY] = MYSQL_BEIJING_TIMEZONE;
}

async function ensureMysqlPoolTimeZone(pool) {
  if (!pool) return pool;
  const rawPool = pool?.pool && typeof pool.pool.on === 'function' ? pool.pool : pool;
  if (rawPool && typeof rawPool.on === 'function' && rawPool[MYSQL_POOL_TIMEZONE_WIRED] !== MYSQL_BEIJING_TIMEZONE) {
    rawPool.on('connection', (connection) => {
      if (!connection || typeof connection.query !== 'function') return;
      connection.query('SET time_zone = ?', [MYSQL_BEIJING_TIMEZONE], (error) => {
        if (!error) {
          connection[MYSQL_SESSION_TIMEZONE_READY] = MYSQL_BEIJING_TIMEZONE;
        }
      });
    });
    rawPool[MYSQL_POOL_TIMEZONE_WIRED] = MYSQL_BEIJING_TIMEZONE;
  }
  if (typeof pool.query === 'function') {
    await pool.query('SET time_zone = ?', [MYSQL_BEIJING_TIMEZONE]);
  }
  return pool;
}

module.exports = {
  BEIJING_TIMEZONE,
  MYSQL_BEIJING_TIMEZONE,
  addDaysToDateKey,
  buildBeijingFutureExpiryMs,
  ensureMysqlPoolTimeZone,
  ensureMysqlSessionTimeZone,
  formatBeijingChineseDate,
  formatBeijingDateTime,
  formatBeijingMonthDay,
  getBeijingCompactTimestamp,
  getBeijingDateKey,
  getFutureBeijingDateKey,
  getBeijingParts,
  normalizeDateOnlyToBeijingString,
  normalizeTimeOnlyString,
  toTimestampMs,
};

module.exports.default = module.exports;
